import React, { useState, useEffect, useCallback } from 'react';
import { puantajQualityApi } from '../api/puantajQualityApi';
import { useAuth } from '../contexts/AuthContext';
import { 
    X, Check, AlertTriangle, Clock, RotateCcw, Plus, 
    ArrowLeftRight, ShieldAlert, CheckCircle2, History, Sparkles, 
    Edit3, Trash2, Calendar, CalendarOff, Moon, EyeOff, Eye
} from 'lucide-react';

const formatLocalYMD = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const ACTION_TYPE_LABELS = {
    ApplySuggestion: 'Akıllı Öneri Uygulandı',
    ManualCorrection: 'Manuel Geçiş Eklendi',
    FlipPassDirection: 'Geçiş Yönü Değiştirildi (Giriş ⇄ Çıkış)',
    ToggleIgnorePass: 'Geçiş Yok Sayma / Geri Yükleme',
    Accept: 'Mevcut Durum Kabul Edildi',
    RevokeAccept: 'Kabul Onayı Kaldırıldı',
    BatchApply: 'Toplu Kalite Düzeltmesi',
    BatchUndo: 'Toplu İşlem Geri Alındı',
    Undo: 'İşlem Geri Alındı'
};

const REVERSIBLE_ACTIONS = new Set([
    'ApplySuggestion',
    'ManualCorrection',
    'FlipPassDirection',
    'ToggleIgnorePass'
]);

const normalizeIssues = (rawJson) => {
    if (!rawJson) return [];
    try {
        const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
        if (!Array.isArray(parsed)) return [];
        return parsed.map((item) => ({
            type: item.type ?? item.Type ?? '',
            message: item.message ?? item.Message ?? '',
            severity: item.severity ?? item.Severity ?? 1
        }));
    } catch {
        return [];
    }
};

const normalizeSuggestions = (rawJson) => {
    if (!rawJson) return [];
    try {
        const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
        if (!Array.isArray(parsed)) return [];
        return parsed.map((s) => {
            const rawPasses = s.proposedPasses ?? s.ProposedPasses ?? [];
            return {
                id: s.id ?? s.Id ?? '',
                type: s.type ?? s.Type ?? '',
                title: s.title ?? s.Title ?? '',
                description: s.description ?? s.Description ?? '',
                why: s.why ?? s.Why ?? null,
                targetPassRecordId: s.targetPassRecordId ?? s.TargetPassRecordId ?? null,
                targetNewRecordType: s.targetNewRecordType ?? s.TargetNewRecordType ?? null,
                proposedPasses: Array.isArray(rawPasses)
                    ? rawPasses.map((pp) => ({
                        time: pp.time ?? pp.Time,
                        recordType: pp.recordType ?? pp.RecordType ?? 0,
                        reason: pp.reason ?? pp.Reason ?? ''
                    }))
                    : []
            };
        });
    } catch {
        return [];
    }
};

const normalizeQualityStatus = (statusVal) => {
    if (statusVal === 0 || statusVal === '0' || statusVal === 'Clean') return 0;
    if (statusVal === 1 || statusVal === '1' || statusVal === 'Warning') return 1;
    if (statusVal === 2 || statusVal === '2' || statusVal === 'Error') return 2;
    if (statusVal === 3 || statusVal === '3' || statusVal === 'Accepted') return 3;
    return 0;
};

const isEntryRecord = (r) => {
    if (!r) return true;
    return r.recordType === 0 || r.recordType === '0' || r.recordType === 'Entry' || r.recordTypeLabel === 'Giriş';
};

export function PuantajQualityDetailDrawer({
    puantajId: propPuantajId,
    data: propData,
    isOpen,
    onClose,
    onRefresh
}) {
    const { hasPermission } = useAuth();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(propData || null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('suggestions'); // suggestions, timeline, manual, logs
    
    // Form / Etkileşim State'leri
    const [acceptNote, setAcceptNote] = useState('');
    const [suggestionNote, setSuggestionNote] = useState('');
    const [timelineNote, setTimelineNote] = useState('');
    const [manualTime, setManualTime] = useState('18:00');
    const [manualRecordType, setManualRecordType] = useState(1); // 0:Giriş, 1:Çıkış
    const [manualDayOffset, setManualDayOffset] = useState(0); // -1: Önceki Gün, 0: Aynı Gün, 1: Ertesi Gün (+1)
    const [autoNightShiftDetected, setAutoNightShiftDetected] = useState(false);
    const [manualNote, setManualNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Satır içi bildirim & onay state'i (window.confirm / alert yerine)
    const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error' | 'info', message: '' }
    const [pendingConfirm, setPendingConfirm] = useState(null); // { title, desc, onConfirm }

    const canAccept = hasPermission('puantaj_quality:accept');
    const canCorrect = hasPermission('puantaj_quality:correct');

    const resolvedPuantajId =
        propPuantajId ||
        propData?.puantaj?.id ||
        propData?.Puantaj?.Id ||
        data?.puantaj?.id ||
        null;

    const showFeedback = (type, message) => {
        setFeedback({ type, message });
        if (type === 'success') {
            setTimeout(() => {
                setFeedback(prev => (prev?.message === message ? null : prev));
            }, 4000);
        }
    };

    const selectDefaultTabForData = useCallback((detailObj) => {
        const p = detailObj?.puantaj || detailObj?.Puantaj;
        const q = p?.qualityDetails || p?.QualityDetails;
        const issues = normalizeIssues(q?.issuesJson ?? q?.IssuesJson);
        const suggestions = normalizeSuggestions(q?.suggestionsJson ?? q?.SuggestionsJson);
        if (issues.length === 0 && suggestions.length === 0) {
            setActiveTab('timeline');
        } else {
            setActiveTab('suggestions');
        }
    }, []);

    const loadDetail = useCallback(async (targetId) => {
        const idToLoad = targetId || resolvedPuantajId;
        if (!idToLoad) return;
        setLoading(true);
        setError(null);
        setPendingConfirm(null);
        try {
            const res = await puantajQualityApi.getDetail(idToLoad);
            setData(res);
            selectDefaultTabForData(res);
        } catch (err) {
            setError(err.message || 'Detay yüklenemedi');
        } finally {
            setLoading(false);
        }
    }, [resolvedPuantajId, selectDefaultTabForData]);

    // Üst bileşen data prop'u geçiyorsa senkronize et, sadece puantajId geçiyorsa API'den çek
    useEffect(() => {
        if (!isOpen) {
            setData(null);
            setFeedback(null);
            setPendingConfirm(null);
            return;
        }

        if (propData && (propData.puantaj || propData.Puantaj)) {
            setData(propData);
            setError(null);
            selectDefaultTabForData(propData);
        } else if (propPuantajId) {
            loadDetail(propPuantajId);
        }
    }, [isOpen, propData, propPuantajId, loadDetail, selectDefaultTabForData]);

    // Gece vardiyası otomatik tespiti: Çıkış seçiliyse ve girilen saat ilk girişten küçükse otomatik +1 Gün seç
    useEffect(() => {
        if (!data?.puantaj) return;
        const p = data.puantaj;
        if (Number(manualRecordType) === 1 && manualTime) {
            const refEntryIso = p.firstEntryTime || p.plannedStartTime;
            if (refEntryIso) {
                const refDate = new Date(refEntryIso);
                const refMinutes = refDate.getHours() * 60 + refDate.getMinutes();
                const [h, m] = manualTime.split(':').map(Number);
                const inputMinutes = (h || 0) * 60 + (m || 0);

                // Örneğin giriş 20:00 (1200 dk) ve girilen çıkış 06:00 (360 dk) ise ertesi gündür
                if (refMinutes >= 16 * 60 && inputMinutes <= 12 * 60) {
                    setManualDayOffset(1);
                    setAutoNightShiftDetected(true);
                    return;
                }
            }
        }
        setAutoNightShiftDetected(false);
    }, [manualTime, manualRecordType, data]);

    if (!isOpen) return null;

    const executeWithRefresh = async (fn, successMsg) => {
        if (!resolvedPuantajId) {
            showFeedback('error', 'Puantaj kimliği (ID) bulunamadı.');
            return;
        }
        setActionLoading(true);
        setPendingConfirm(null);
        setFeedback(null);
        try {
            const updatedData = await fn();
            if (updatedData && (updatedData.puantaj || updatedData.Puantaj)) {
                setData(updatedData);
            } else {
                await loadDetail(resolvedPuantajId);
            }
            if (onRefresh) onRefresh();
            if (successMsg) showFeedback('success', successMsg);
        } catch (err) {
            showFeedback('error', err.response?.data?.Message || err.response?.data?.message || err.message || 'İşlem sırasında hata oluştu.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAccept = () => {
        executeWithRefresh(
            async () => {
                await puantajQualityApi.accept(resolvedPuantajId, acceptNote);
                setAcceptNote('');
                return null;
            },
            'Puantaj durumu onaylandı ve "Kabul Edildi" olarak işaretlendi.'
        );
    };

    const handleRevokeAccept = () => {
        executeWithRefresh(
            async () => {
                await puantajQualityApi.revokeAccept(resolvedPuantajId, 'Kabul durumu kaldırıldı');
                return null;
            },
            'Kabul onayı kaldırıldı; kalite durumu yeniden değerlendirildi.'
        );
    };

    const handleApplySuggestion = (suggestion) => {
        executeWithRefresh(
            async () => {
                const res = await puantajQualityApi.applySuggestion(resolvedPuantajId, suggestion.id, suggestionNote);
                setSuggestionNote('');
                return res;
            },
            `"${suggestion.title}" önerisi başarıyla uygulandı ve puantaj yeniden hesaplandı.`
        );
    };

    const handleUndo = (logId) => {
        executeWithRefresh(
            async () => await puantajQualityApi.undo(resolvedPuantajId, logId),
            'İşlem başarıyla geri alındı ve puantaj yeniden hesaplandı.'
        );
    };

    const handleToggleIgnorePass = (record) => {
        executeWithRefresh(
            async () => {
                const res = await puantajQualityApi.toggleIgnorePass(resolvedPuantajId, record.id, timelineNote);
                setTimelineNote('');
                return res;
            },
            `${new Date(record.recordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} hareketi ${record.isIgnored ? 'tekrar aktif edildi' : 'yok sayıldı'}.`
        );
    };

    const handleFlipPassDirection = (record) => {
        const entry = isEntryRecord(record);
        const fromLabel = entry ? 'Giriş' : 'Çıkış';
        const toLabel = entry ? 'Çıkış' : 'Giriş';
        const timeStr = new Date(record.recordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

        setPendingConfirm({
            title: `Geçiş Yönünü Değiştir (${fromLabel} ⇄ ${toLabel})`,
            desc: `${timeStr} saatli "${fromLabel}" kaydının yönü "${toLabel}" olarak değiştirilecek ve hareket notuna "Yön değiştirilmiştir" ibaresi eklenecektir.`,
            onConfirm: () => executeWithRefresh(
                async () => {
                    const res = await puantajQualityApi.flipPassDirection(resolvedPuantajId, record.id, timelineNote);
                    setTimelineNote('');
                    return res;
                },
                `${timeStr} hareketinin yönü "${fromLabel} → ${toLabel}" olarak değiştirildi ve puantaj güncellendi.`
            )
        });
    };

    const handleManualCorrection = (e) => {
        e.preventDefault();
        if (!manualTime || !data?.puantaj) return;

        const baseDatePart = data.puantaj.date.split('T')[0];
        const [y, m, d] = baseDatePart.split('-').map(Number);
        const targetDateObj = new Date(y, m - 1, d + Number(manualDayOffset));
        const targetDateStr = formatLocalYMD(targetDateObj);
        const localIso = `${targetDateStr}T${manualTime}:00`;

        const typeLabel = Number(manualRecordType) === 0 ? 'Giriş' : 'Çıkış';
        const dayLabel = Number(manualDayOffset) === 1 ? ' (Ertesi Gün +1)' : (Number(manualDayOffset) === -1 ? ' (Önceki Gün -1)' : '');

        executeWithRefresh(
            async () => {
                const res = await puantajQualityApi.manualCorrection(resolvedPuantajId, localIso, manualRecordType, manualNote);
                setManualNote('');
                return res;
            },
            `${manualTime}${dayLabel} saatine manuel "${typeLabel}" kaydı eklendi ve puantaj yeniden hesaplandı.`
        );
    };

    const renderContent = () => {
        if (loading && !data) {
            return (
                <div className="flex flex-col items-center justify-center flex-1 p-12 space-y-4">
                    <div className="relative">
                        <div className="w-12 h-12 border-4 border-indigo-200 dark:border-indigo-900 rounded-full animate-pulse"></div>
                        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Kalite analizi ve akıllı öneriler yükleniyor...</p>
                </div>
            );
        }
        if (error) {
            return (
                <div className="p-6 m-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl text-center">
                    <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                    <h4 className="font-bold text-red-800 dark:text-red-300">Veri Yüklenemedi</h4>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
                    <button onClick={() => loadDetail(resolvedPuantajId)} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bold">Tekrar Dene</button>
                </div>
            );
        }
        if (!data || !data.puantaj) return null;

        const { puantaj, logs, timelineRecords, activeLeavesOnDay = [] } = data;
        const previousDayRecords = data.previousDayRecords || data.PreviousDayRecords || [];
        const quality = puantaj.qualityDetails;
        const issues = normalizeIssues(quality?.issuesJson);
        const suggestions = normalizeSuggestions(quality?.suggestionsJson);
        const appliedIds = (() => {
            if (!quality?.appliedSuggestionsJson) return [];
            try {
                const parsed = JSON.parse(quality.appliedSuggestionsJson);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        })();
        const qStatus = normalizeQualityStatus(puantaj.dataQualityStatus);
        const puantajDateStr = puantaj.date.split('T')[0];

        const getRecordDayDiffBadge = (recordTimeIso) => {
            if (!recordTimeIso) return null;
            const recDate = new Date(recordTimeIso);
            const recDateStr = formatLocalYMD(recDate);
            if (recDateStr === puantajDateStr) return null;
            const [py, pm, pd] = puantajDateStr.split('-').map(Number);
            const pDateObj = new Date(py, pm - 1, pd);
            const rDateObj = new Date(recDate.getFullYear(), recDate.getMonth(), recDate.getDate());
            const diffDays = Math.round((rDateObj - pDateObj) / (1000 * 60 * 60 * 24));
            if (diffDays > 0) {
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 text-[9px] font-black uppercase tracking-tight">
                        <Moon size={9} /> +{diffDays} Gün ({recDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })})
                    </span>
                );
            }
            if (diffDays < 0) {
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 text-[9px] font-black uppercase tracking-tight">
                        {diffDays} Gün ({recDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })})
                    </span>
                );
            }
            return null;
        };

        const [baseY, baseM, baseD] = puantajDateStr.split('-').map(Number);
        const sameDayDate = new Date(baseY, baseM - 1, baseD);
        const nextDayDate = new Date(baseY, baseM - 1, baseD + 1);
        const prevDayDate = new Date(baseY, baseM - 1, baseD - 1);

        return (
            <div className="flex-1 flex flex-col overflow-hidden bg-gray-50/50 dark:bg-gray-900/20">
                {/* Summary Header Card */}
                <div className="p-5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 shadow-sm space-y-4 shrink-0">
                    <div className="flex justify-between items-start gap-2">
                        <div>
                            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-bold text-xs uppercase tracking-wider mb-1">
                                <Calendar size={13} />
                                <span>{new Date(puantaj.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })}</span>
                            </div>
                            <h3 className="text-lg font-black text-gray-900 dark:text-gray-100 leading-tight">
                                {puantaj.employee?.firstName} {puantaj.employee?.lastName}
                            </h3>
                            {(puantaj.shiftName || puantaj.shiftCode) && (
                                <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
                                    <span>Vardiya: {puantaj.shiftName || puantaj.shiftCode}</span>
                                    {puantaj.plannedStartTime && puantaj.plannedEndTime && (
                                        <span className="font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded text-[10px]">
                                            {new Date(puantaj.plannedStartTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                            {' - '}
                                            {new Date(puantaj.plannedEndTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                            {qStatus === 0 && (
                                <span className="px-3 py-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] font-black uppercase rounded-full border border-emerald-200 dark:border-emerald-800">
                                    Temiz
                                </span>
                            )}
                            {qStatus === 1 && (
                                <span className="px-3 py-1 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] font-black uppercase rounded-full border border-amber-200 dark:border-amber-800 animate-pulse">
                                    Uyarı Var
                                </span>
                            )}
                            {qStatus === 2 && (
                                <span className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 text-[10px] font-black uppercase rounded-full border border-red-200 dark:border-red-800 animate-bounce">
                                    Kritik Hata
                                </span>
                            )}
                            {qStatus === 3 && (
                                <div className="flex items-center gap-1.5">
                                    <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 text-[10px] font-black uppercase rounded-full border border-blue-200 dark:border-blue-800 flex items-center gap-1">
                                        <Check size={10} className="stroke-[3]" /> Kabul Edildi
                                    </span>
                                    {canAccept && (
                                        <button 
                                            onClick={handleRevokeAccept}
                                            disabled={actionLoading}
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                            title="Kabul Durumunu Kaldır"
                                        >
                                            <RotateCcw size={14} />
                                        </button>
                                    )}
                                </div>
                            )}
                            <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500">Durum: {puantaj.status || 'Normal'}</span>
                        </div>
                    </div>

                    {/* Quick Stats Grid */}
                    <div className="grid grid-cols-3 gap-2 pt-1">
                        <div className="bg-gray-50 dark:bg-gray-900/40 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700/50">
                            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase block">İlk Giriş</span>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className="text-sm font-black text-gray-800 dark:text-gray-200 font-mono">
                                    {puantaj.firstEntryTime ? new Date(puantaj.firstEntryTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                </span>
                                {getRecordDayDiffBadge(puantaj.firstEntryTime)}
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/40 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700/50">
                            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase block">Son Çıkış</span>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className="text-sm font-black text-gray-800 dark:text-gray-200 font-mono">
                                    {puantaj.lastExitTime ? new Date(puantaj.lastExitTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                </span>
                                {getRecordDayDiffBadge(puantaj.lastExitTime)}
                            </div>
                        </div>
                        <div className="bg-indigo-50/50 dark:bg-indigo-900/20 p-2.5 rounded-xl border border-indigo-100/50 dark:border-indigo-800/30">
                            <span className="text-[10px] font-bold text-indigo-400 dark:text-indigo-500 uppercase block">Fiili / Net</span>
                            <span className="text-sm font-black text-indigo-700 dark:text-indigo-300 mt-0.5 block">
                                {Math.floor((puantaj.actualWorkDurationMinutes || 0) / 60)}sa {(puantaj.actualWorkDurationMinutes || 0) % 60}dk
                            </span>
                        </div>
                    </div>

                    {/* Issues Banner List */}
                    {issues.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                            {issues.map((iss, idx) => (
                                <div key={idx} className={`flex items-start gap-2.5 p-2.5 rounded-xl text-xs font-medium border ${
                                    iss.severity === 2 
                                        ? 'bg-red-50/80 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50' 
                                        : 'bg-amber-50/80 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800/50'
                                }`}>
                                    <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                                    <div className="flex-1 leading-snug">{iss.message}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Inline Feedback / Toast */}
                    {feedback && (
                        <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border ${
                            feedback.type === 'error'
                                ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                        }`}>
                            <div className="flex items-center gap-2">
                                {feedback.type === 'error' ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
                                <span>{feedback.message}</span>
                            </div>
                            <button onClick={() => setFeedback(null)} className="opacity-60 hover:opacity-100">
                                <X size={14} />
                            </button>
                        </div>
                    )}

                    {/* Inline Confirmation Box */}
                    {pendingConfirm && (
                        <div className="p-3.5 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 space-y-2.5">
                            <div className="font-black text-xs text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                                <ArrowLeftRight size={14} className="text-indigo-600 dark:text-indigo-400" />
                                {pendingConfirm.title}
                            </div>
                            <p className="text-[11px] text-indigo-800/90 dark:text-indigo-200/80 leading-relaxed">
                                {pendingConfirm.desc}
                            </p>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPendingConfirm(null)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                                >
                                    Vazgeç
                                </button>
                                <button
                                    type="button"
                                    disabled={actionLoading}
                                    onClick={pendingConfirm.onConfirm}
                                    className="px-3.5 py-1.5 rounded-lg text-xs font-black bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                                >
                                    {actionLoading ? 'Uygulanıyor...' : 'Onayla ve Uygula'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Quick Accept Bar */}
                    {canAccept && qStatus !== 0 && qStatus !== 3 && (
                        <div className="pt-1 flex gap-2">
                            <input 
                                type="text" 
                                placeholder="Mevcut haliyle kabul etme notu (isteğe bağlı)..." 
                                value={acceptNote} 
                                onChange={e => setAcceptNote(e.target.value)}
                                className="flex-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-gray-200"
                            />
                            <button 
                                onClick={handleAccept}
                                disabled={actionLoading}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-blue-500/10 active:scale-95 disabled:opacity-50"
                            >
                                Kabul Et
                            </button>
                        </div>
                    )}
                </div>

                {/* Navigation Tabs */}
                <div className="flex bg-white dark:bg-gray-800 px-4 border-b border-gray-100 dark:border-gray-700 shrink-0 overflow-x-auto">
                    {[
                        { id: 'suggestions', label: 'Akıllı Öneriler', icon: Sparkles, count: suggestions.length },
                        { id: 'timeline', label: 'Hareketler', icon: Clock, count: timelineRecords?.length },
                        { id: 'manual', label: 'Manuel Ekle', icon: Edit3 },
                        { id: 'logs', label: 'Geçmiş', icon: History, count: logs?.length }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-1.5 py-3.5 px-3 border-b-2 text-xs font-bold transition-all relative whitespace-nowrap ${
                                activeTab === tab.id 
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' 
                                    : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                            }`}
                        >
                            <tab.icon size={14} />
                            <span>{tab.label}</span>
                            {tab.count !== undefined && tab.count > 0 && (
                                <span className={`ml-0.5 px-1.5 py-0.2 rounded-full text-[10px] ${
                                    activeTab === tab.id 
                                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/60 dark:text-indigo-300' 
                                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Tab Contents */}
                <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
                    
                    {/* 1. AKILLI ÖNERİLER */}
                    {activeTab === 'suggestions' && (
                        <div className="space-y-4">
                            {/* Seçenek 2: Önceki Günün Gece Mesaisine Ait (Tüketilmiş) Hareket Bilgi Bandı */}
                            {previousDayRecords.length > 0 && (
                                <div className="p-3.5 rounded-2xl bg-slate-100/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300 font-black text-xs uppercase tracking-wider">
                                            <Moon size={14} />
                                            <span>Önceki Gün Mesaisine Ait Hareket ({previousDayRecords.length})</span>
                                        </div>
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200/80 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                            Kilitli · Önceki Güne İşlendi
                                        </span>
                                    </div>
                                    {previousDayRecords.map((pr) => (
                                        <div key={pr.id || pr.Id} className="bg-white/80 dark:bg-gray-900/50 rounded-xl p-2.5 border border-slate-200/80 dark:border-slate-700/60 text-xs space-y-1">
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px] font-black uppercase">
                                                        {pr.recordTypeLabel || pr.RecordTypeLabel || 'Çıkış'}
                                                    </span>
                                                    <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                                                        {new Date(pr.recordTime || pr.RecordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </span>
                                                </div>
                                                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400">
                                                    {pr.previousShiftDate || pr.PreviousShiftDate} ({pr.previousShiftRange || pr.PreviousShiftRange})
                                                </span>
                                            </div>
                                            <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                                                ℹ️ Bu hareket, <strong>{pr.previousShiftDate || pr.PreviousShiftDate}</strong> tarihli mesainin (<strong>{pr.previousShiftRange || pr.PreviousShiftRange}</strong>) devamı olduğu için <strong>{pr.previousShiftDate || pr.PreviousShiftDate}</strong> puantajına işlenmiştir. Bu günün hesaplamasına ve kalite kontrolüne dahil edilmez.
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Bu Güne Ait Onaylı İzin Özeti */}
                            {activeLeavesOnDay.length > 0 && (
                                <div className="p-3.5 rounded-2xl bg-rose-50/80 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 space-y-2">
                                    <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300 font-black text-xs uppercase tracking-wider">
                                        <CalendarOff size={15} />
                                        <span>Bu Güne Ait Onaylı İzin Kaydı ({activeLeavesOnDay.length})</span>
                                    </div>
                                    {activeLeavesOnDay.map((lv) => {
                                        const sDate = new Date(lv.baslangicTarihi);
                                        const eDate = new Date(lv.bitisTarihi);
                                        const isMultiDay = formatLocalYMD(sDate) !== formatLocalYMD(eDate);
                                        const spanDays = Math.max(1, Math.round((eDate - sDate) / (1000 * 60 * 60 * 24)) + 1);
                                        return (
                                            <div key={lv.id} className="bg-white/90 dark:bg-gray-900/60 rounded-xl p-2.5 border border-rose-100 dark:border-rose-900/40 text-xs space-y-1">
                                                <div className="flex justify-between items-center gap-2">
                                                    <div>
                                                        <span className="font-bold text-gray-800 dark:text-gray-100">{lv.izinTuruAdi}</span>
                                                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300 font-bold">
                                                            {isMultiDay ? `Çok Günlü (${spanDays} Gün)` : lv.unitUsed}
                                                        </span>
                                                    </div>
                                                    <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                                                        {sDate.toLocaleDateString('tr-TR')}
                                                        {isMultiDay ? ` - ${eDate.toLocaleDateString('tr-TR')}` : ''}
                                                    </div>
                                                </div>
                                                {isMultiDay && (
                                                    <div className="text-[10px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 px-2 py-1 rounded-lg font-medium">
                                                        💡 <strong>Akıllı Çok Günlü İzin Koruması:</strong> "İzni İptal Et / Günü Düş" seçildiğinde {spanDays} günlük iznin tamamı silinmez; sadece <strong>{new Date(puantaj.date).toLocaleDateString('tr-TR')}</strong> günü izinden çıkarılıp 1 günlük bakiye iade edilir, diğer izin günleri korunur.
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {suggestions.length === 0 ? (
                                <div className="text-center py-12 px-6 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2 opacity-60" />
                                    <h5 className="font-bold text-gray-700 dark:text-gray-300 text-sm">Her Şey Yolunda</h5>
                                    <p className="text-xs text-gray-400 mt-1">Bu gün için sistem tarafından üretilen otomatik bir düzeltme önerisi bulunmuyor.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="bg-indigo-50/50 dark:bg-indigo-900/10 p-3 rounded-xl border border-indigo-100 dark:border-indigo-900/30 flex items-center gap-2.5 text-[11px] text-indigo-700 dark:text-indigo-300">
                                        <Sparkles size={16} className="shrink-0 text-indigo-500" />
                                        <span>Öneriyi uyguladığınızda ilgili hafta puantajı otomatik olarak yeniden hesaplanır.</span>
                                    </div>
                                    <div className="mb-2">
                                        <input
                                            type="text"
                                            value={suggestionNote}
                                            onChange={(e) => setSuggestionNote(e.target.value)}
                                            placeholder="Öneri uygulama notu (isteğe bağlı, denetim kaydına yazılır)..."
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    {suggestions.map(sugg => {
                                        const isApplied = appliedIds.includes(sugg.id);
                                        const isLeaveCancel = sugg.type === 'CancelLeaveAndCountWork';
                                        const isLeaveIgnore = sugg.type === 'IgnorePassesOnLeaveDay';
                                        const isDirectionFlip = sugg.type === 'FlipEntryToExit' || sugg.type === 'FlipExitToEntry';
                                        return (
                                            <div key={sugg.id} className={`p-4 rounded-2xl border transition-all ${
                                                isApplied 
                                                    ? 'bg-emerald-50/20 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800/40 opacity-75' 
                                                    : isLeaveCancel
                                                        ? 'bg-rose-50/40 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800/60 shadow-sm hover:border-rose-400'
                                                        : isDirectionFlip
                                                            ? 'bg-purple-50/40 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800/60 shadow-sm hover:border-purple-400'
                                                            : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-800'
                                            }`}>
                                                <div className="flex justify-between items-start gap-3">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {isDirectionFlip && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-[10px] font-black uppercase">
                                                                    <ArrowLeftRight size={11} /> Yön Düzeltme
                                                                </span>
                                                            )}
                                                            <h4 className="font-black text-gray-800 dark:text-gray-100 text-sm tracking-tight">{sugg.title}</h4>
                                                            {isApplied && (
                                                                <span className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-2 py-0.5 rounded-md font-bold">
                                                                    Uygulandı
                                                                </span>
                                                            )}
                                                        </div>
                                                        {sugg.why && (
                                                            <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 leading-relaxed">
                                                                Neden: {sugg.why}
                                                            </p>
                                                        )}
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{sugg.description}</p>
                                                    </div>
                                                    {canCorrect && !isApplied && (
                                                        <button 
                                                            onClick={() => handleApplySuggestion(sugg)}
                                                            disabled={actionLoading}
                                                            className={`shrink-0 text-white text-xs font-black uppercase tracking-wider px-4 py-2 rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-50 ${
                                                                isLeaveCancel
                                                                    ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/20'
                                                                    : isLeaveIgnore
                                                                        ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/20'
                                                                        : isDirectionFlip
                                                                            ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/20'
                                                                            : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20'
                                                            }`}
                                                        >
                                                            Uygula
                                                        </button>
                                                    )}
                                                </div>
                                                {sugg.proposedPasses && sugg.proposedPasses.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-gray-50 dark:border-gray-700/50 flex flex-wrap gap-2">
                                                        {sugg.proposedPasses.map((pp, i) => (
                                                            <div key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 dark:bg-gray-900 rounded-lg text-[10px] font-bold text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">
                                                                <Plus size={10} className="text-indigo-500" />
                                                                <span>{pp.recordType === 0 || pp.recordType === 'Entry' ? 'Giriş' : 'Çıkış'}:</span>
                                                                <span className="text-gray-900 dark:text-gray-200 font-mono">
                                                                    {new Date(pp.time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                                {getRecordDayDiffBadge(pp.time)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}

                    {/* 2. HAREKETLER (TIMELINE) — TEKİL KAYIT MÜDAHALESİ EKLENDİ */}
                    {activeTab === 'timeline' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-1 flex-wrap gap-1">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Değerlendirme Penceresi</span>
                                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-md font-mono">
                                    {new Date(data.windowStart).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    {' → '}
                                    {new Date(data.windowEnd).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>

                            {/* Seçenek 2: Önceki Günün Gece Mesaisine Ait (Kilitli/Soluk) Hareketler */}
                            {previousDayRecords.length > 0 && (
                                <div className="p-3.5 rounded-2xl bg-slate-100/75 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700 space-y-2.5 opacity-85">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                                            <Moon size={13} />
                                            <span>Önceki Gün Mesaisine Ait Hareket (Kilitli)</span>
                                        </div>
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-indigo-100/80 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                                            {previousDayRecords[0]?.previousShiftDate || previousDayRecords[0]?.PreviousShiftDate} Puantajında Hesaplandı
                                        </span>
                                    </div>
                                    {previousDayRecords.map((pr) => (
                                        <div key={pr.id || pr.Id} className="p-3 rounded-xl bg-white/80 dark:bg-gray-900/60 border border-slate-200/80 dark:border-slate-700/60 space-y-1.5">
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                        {pr.recordTypeLabel || pr.RecordTypeLabel || 'Çıkış'}
                                                    </span>
                                                    <span className="text-sm font-mono font-bold text-slate-700 dark:text-slate-200">
                                                        {new Date(pr.recordTime || pr.RecordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </span>
                                                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                                        ({pr.previousShiftDate || pr.PreviousShiftDate} · {pr.previousShiftRange || pr.PreviousShiftRange})
                                                    </span>
                                                </div>
                                                <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-md">
                                                    {pr.entrySourceLabel || pr.EntrySourceLabel || 'Cihaz'}
                                                </span>
                                            </div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
                                                ℹ️ Bu hareket <strong>{pr.previousShiftDate || pr.PreviousShiftDate}</strong> gece mesaisinin kapanışı olduğu için o günün puantajına işlenmiştir; bu günün hareketlerine dahil edilmez.
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {canCorrect && timelineRecords && timelineRecords.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 p-3 rounded-2xl border border-gray-100 dark:border-gray-700 space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-wider block">
                                        Tekil Hareket Müdahale Notu (Yön Değiştirme / Yok Sayma İçin)
                                    </label>
                                    <input
                                        type="text"
                                        value={timelineNote}
                                        onChange={(e) => setTimelineNote(e.target.value)}
                                        placeholder="Örn: Çıkarken yanlışlıkla giriş turnikesinden geçmiş..."
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            )}

                            {!timelineRecords || timelineRecords.length === 0 ? (
                                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                    <p className="text-xs text-gray-400 font-medium">Bu zaman aralığında geçiş kaydı bulunamadı.</p>
                                </div>
                            ) : (
                                <div className="relative pl-6 space-y-3 before:content-[''] before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-indigo-100 dark:before:bg-gray-700">
                                    {timelineRecords.map((r) => {
                                        const entry = isEntryRecord(r);
                                        return (
                                            <div key={r.id} className={`relative p-3.5 rounded-2xl border transition-all ${
                                                r.isIgnored 
                                                    ? 'bg-gray-100/40 border-gray-200 dark:bg-gray-800/20 dark:border-gray-800 opacity-65' 
                                                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 shadow-sm'
                                            }`}>
                                                {/* Dot */}
                                                <div className={`absolute -left-[21px] top-4 w-4 h-4 rounded-full border-2 border-white dark:border-gray-900 shadow-sm ${
                                                    r.isIgnored ? 'bg-gray-400' : (entry ? 'bg-emerald-500' : 'bg-rose-500')
                                                }`}></div>

                                                <div className="flex justify-between items-start gap-2">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                                                            entry 
                                                                ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300' 
                                                                : 'text-rose-700 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-300'
                                                        } ${r.isIgnored ? 'line-through' : ''}`}>
                                                            {r.recordTypeLabel || (entry ? 'Giriş' : 'Çıkış')}
                                                        </span>
                                                        <span className={`text-sm font-mono font-bold text-gray-900 dark:text-gray-100 ${r.isIgnored ? 'line-through' : ''}`}>
                                                            {new Date(r.recordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                        </span>
                                                        {getRecordDayDiffBadge(r.recordTime)}
                                                    </div>
                                                    <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md">
                                                        {r.entrySourceLabel}
                                                    </span>
                                                </div>

                                                {(r.deviceName || r.notes) && (
                                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                                                        {r.deviceName && <div><strong className="font-semibold">Cihaz:</strong> {r.deviceName}</div>}
                                                        {r.notes && <div className="text-indigo-600 dark:text-indigo-400 font-medium"><strong className="font-semibold">Not:</strong> {r.notes}</div>}
                                                    </div>
                                                )}

                                                {r.isIgnored && (
                                                    <div className="mt-2 pt-2 border-t border-gray-200/50 dark:border-gray-700/50 flex items-center gap-1.5 text-[10px] text-red-500 dark:text-red-400 font-bold italic">
                                                        <Trash2 size={11} />
                                                        <span>Yok Sayıldı: {r.ignoreReason || 'Sebep belirtilmedi'}</span>
                                                    </div>
                                                )}

                                                {/* Tekil Hareket Müdahale Butonları */}
                                                {canCorrect && (
                                                    <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-700/60 flex flex-wrap items-center justify-end gap-2">
                                                        {!r.isIgnored && (
                                                            <button
                                                                type="button"
                                                                disabled={actionLoading}
                                                                onClick={() => handleFlipPassDirection(r)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 dark:text-purple-300 border border-purple-200/70 dark:border-purple-800/50 transition-colors"
                                                                title="Bu kaydın giriş/çıkış yönünü değiştir ve not düş"
                                                            >
                                                                <ArrowLeftRight size={12} />
                                                                Yön Değiştir ({entry ? 'Giriş → Çıkış' : 'Çıkış → Giriş'})
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            disabled={actionLoading}
                                                            onClick={() => handleToggleIgnorePass(r)}
                                                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
                                                                r.isIgnored
                                                                    ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                                                                    : 'bg-gray-50 hover:bg-red-50 text-gray-600 hover:text-red-600 border-gray-200 hover:border-red-200 dark:bg-gray-900/50 dark:text-gray-300 dark:border-gray-700'
                                                            }`}
                                                        >
                                                            {r.isIgnored ? (
                                                                <>
                                                                    <Eye size={12} /> Kaydı Geri Yükle (Aktifleştir)
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <EyeOff size={12} /> Yok Say
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 3. MANUEL DÜZELTME — GECE VARDİYASI (+1 GÜN) DESTEKLİ */}
                    {activeTab === 'manual' && (
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-5">
                            <div className="space-y-1">
                                <h4 className="font-black text-gray-900 dark:text-gray-100 text-sm">Manuel Geçiş Kaydı Ekle</h4>
                                <p className="text-xs text-gray-400">Okutulması unutulan giriş veya çıkış hareketini (gece vardiyası için ertesi gün dahil) ekleyin.</p>
                            </div>

                            {!canCorrect ? (
                                <div className="p-4 bg-red-50 text-red-600 rounded-xl text-xs font-bold flex items-center gap-2">
                                    <ShieldAlert size={16} />
                                    <span>Bu işlemi yapmak için yetkiniz bulunmamaktadır.</span>
                                </div>
                            ) : (
                                <form onSubmit={handleManualCorrection} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase ml-1">Kayıt Yönü</label>
                                            <select 
                                                value={manualRecordType} 
                                                onChange={e => setManualRecordType(Number(e.target.value))}
                                                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-2.5 text-xs font-bold focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:text-gray-200"
                                            >
                                                <option value={0}>Giriş Hareketi</option>
                                                <option value={1}>Çıkış Hareketi</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase ml-1">Saat</label>
                                            <input 
                                                type="time" 
                                                value={manualTime} 
                                                onChange={e => setManualTime(e.target.value)}
                                                required 
                                                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-2.5 text-xs font-bold font-mono focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:text-gray-200"
                                            />
                                        </div>
                                    </div>

                                    {/* Gün Seçici (Gece Vardiyası +1 Gün / Önceki Gün -1 Desteği) */}
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase ml-1 flex items-center gap-1.5">
                                            <Moon size={12} className="text-indigo-500" />
                                            Hareketin Ekleneceği Takvim Günü
                                        </label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { offset: -1, title: 'Önceki Gün (-1)', sub: prevDayDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) },
                                                { offset: 0, title: 'Aynı Gün', sub: sameDayDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) },
                                                { offset: 1, title: 'Ertesi Gün (+1)', sub: nextDayDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) }
                                            ].map(opt => (
                                                <button
                                                    key={opt.offset}
                                                    type="button"
                                                    onClick={() => {
                                                        setManualDayOffset(opt.offset);
                                                        setAutoNightShiftDetected(false);
                                                    }}
                                                    className={`p-2 rounded-xl border text-center transition-all ${
                                                        manualDayOffset === opt.offset
                                                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
                                                            : 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    <div className="text-[11px] font-black leading-tight">{opt.title}</div>
                                                    <div className={`text-[10px] mt-0.5 font-mono ${manualDayOffset === opt.offset ? 'text-indigo-100' : 'text-gray-400'}`}>
                                                        {opt.sub}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>

                                        {autoNightShiftDetected && manualDayOffset === 1 && (
                                            <div className="mt-2 p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800/50 text-[11px] text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                                                <Moon size={14} className="shrink-0 text-indigo-500" />
                                                <span>
                                                    <strong>Gece Vardiyası Algılandı:</strong> Girilen çıkış saati ({manualTime}), personelin akşam girişinden küçük olduğu için otomatik olarak <strong>Ertesi Gün (+1 Gün — {nextDayDate.toLocaleDateString('tr-TR')})</strong> seçildi.
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase ml-1">Açıklama / Neden</label>
                                        <input 
                                            type="text" 
                                            value={manualNote} 
                                            onChange={e => setManualNote(e.target.value)}
                                            placeholder="Örn: Cihaz arızası, kart unutma, gece vardiyası çıkışı..."
                                            required 
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-2.5 text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:text-gray-200"
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        disabled={actionLoading}
                                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-500/20 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {actionLoading ? 'Kaydediliyor...' : 'Kaydı Ekle ve Puantajı Hesapla'}
                                    </button>
                                </form>
                            )}
                        </div>
                    )}

                    {/* 4. İŞLEM GEÇMİŞİ (LOGS) */}
                    {activeTab === 'logs' && (
                        <div className="space-y-3">
                            {!logs || logs.length === 0 ? (
                                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <History className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                    <p className="text-xs text-gray-400 font-medium">Bu güne ait düzeltme geçmişi bulunmuyor.</p>
                                </div>
                            ) : (
                                logs.map(l => {
                                    const canUndo = canCorrect && REVERSIBLE_ACTIONS.has(l.actionType);
                                    return (
                                        <div key={l.id} className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-2">
                                            <div className="flex justify-between items-start gap-2">
                                                <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-tighter">
                                                    {ACTION_TYPE_LABELS[l.actionType] || l.actionType}
                                                </span>
                                                <span className="text-[10px] font-bold text-gray-400">
                                                    {new Date(l.performedAt).toLocaleString('tr-TR')}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-end gap-2">
                                                <div className="space-y-1">
                                                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                        <strong>Yapan:</strong> {l.performedByUser?.username || l.performedByUserId}
                                                    </div>
                                                    {l.note && (
                                                        <div className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 px-2.5 py-1 rounded-lg border border-gray-100 dark:border-gray-800">
                                                            {l.note}
                                                        </div>
                                                    )}
                                                </div>
                                                {canUndo && (
                                                    <button 
                                                        onClick={() => handleUndo(l.id)}
                                                        disabled={actionLoading}
                                                        className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-amber-600 hover:text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800/40 transition-all active:scale-95 shrink-0"
                                                    >
                                                        <RotateCcw size={10} /> Geri Al
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-300">
            <div className="w-full max-w-lg bg-white dark:bg-gray-800 h-full shadow-2xl flex flex-col border-l border-gray-100 dark:border-gray-700 animate-in slide-in-from-right duration-300">
                {/* Drawer Header */}
                <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl">
                            <Sparkles size={18} />
                        </div>
                        <div>
                            <h2 className="text-base font-black text-gray-900 dark:text-gray-100 tracking-tight">Puantaj Kalite & Akıllı Çözüm Merkezi</h2>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Veri Doğrulama · Yön Değiştirme · İzin & Mesai Analizi</p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
                    >
                        <X size={20} />
                    </button>
                </div>

                {renderContent()}
            </div>
        </div>
    );
}

export default PuantajQualityDetailDrawer;
